<template>
  <VDialog
    v-model="innerValue"
    v-bind="$attrs"
    :aria-labelledby="labelledBy"
    :width="width"
    :keep-content-alive="false"
    scrollable
    z-index="2147483640"
    @click:outside="close"
    @after-leave="restoreFocus"
  >
    <template v-if="$slots.activator" #activator="slotData">
      <slot name="activator" v-bind="slotData" />
    </template>
    <VCard
      ref="card"
      class="text-center pa-2 confirm-dialog"
      :max-height="cardMaxHeight"
      data-testid="confirm-dialog"
    >
      <VRow no-gutters>
        <VCol cols="12" class="text-right">
          <SBtn
            color="primary"
            text
            :class="{
              'sticky-close': stickyClose
            }"
            data-testid="close-dialog"
            :aria-label="ariaClose"
            @click="close"
          >
            <VIcon size="24">{{ icons.mdiClose }}</VIcon>
          </SBtn>
        </VCol>
      </VRow>
      <VCardTitle
        v-if="title"
        class="text-uppercase font-weight-black pa-0"
      >
        <VRow no-gutters>
          <VCol cols="12" class="px-4 py-2">
            <h4
              :id="titleId"
              :class="textClass"
              data-testid="dialog-title"
            >
              {{ title }}
            </h4>
          </VCol>
        </VRow>
      </VCardTitle>
      <VCardText
        v-if="text"
        class="font14 py-2"
        :class="textClass ? textClass : 'text-primary'"
      >
        {{ text }}
      </VCardText>
      <slot />
      <VCardActions v-if="cancelText">
        <VRow no-gutters>
          <VCol cols="12">
            <SBtn
              class="mb-3"
              link-small
              data-testid="confirm-dialog-btn-cancel"
              @click="close"
            >
              {{ cancelText }}
            </SBtn>
          </VCol>
        </VRow>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<script lang="ts" setup>
import { mdiClose } from '@mdi/js'
import SBtn from '../components/SBtn.vue'

interface Props {
 title?: string
 text?: string
 cancelText?: string
 width?: number
 showDialog: boolean
 stickyClose?: boolean
 cardMaxHeight?: string
 ariaClose?: string
 textClass?: string
 /**
  * Also emit `close` when VOverlay closes the dialog on Escape. Off by default,
  * matching nuxt2's SDialog, because owners treat `close` differently: on /join
  * it clears the join session and reloads home, and Escape there only hides the
  * modal. Turn it on for a dialog whose `close` handler is the only way its
  * owner learns it is gone (a dialog with a fixed `show-dialog`).
  */
 emitCloseOnEscape?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  width: 325,
  ariaClose: 'Close',
  title: undefined,
  text: undefined,
  cancelText: undefined,
  cardMaxHeight: undefined,
  textClass: undefined,
  emitCloseOnEscape: false
})

const emits = defineEmits<{
  (e: 'update:showDialog', show: boolean): void
  (e: 'close'): void
}>()

defineOptions({
  inheritAttrs: false
})

const icons = { mdiClose }
const innerValue = ref(props.showDialog)

// VOverlay puts role="dialog" + aria-modal on its root but names it from
// nothing, so a screen reader announced an unnamed "dialog" (WCAG 4.1.2). Point
// it at the visible title. An owner's own aria-labelledby / aria-label wins.
const attrs = useAttrs()
const titleId = useId()
const labelledBy = computed(() => {
  if (attrs['aria-labelledby'] !== undefined) return attrs['aria-labelledby'] as string
  if (attrs['aria-label'] !== undefined) return undefined
  return props.title ? titleId : undefined
})

// Set while close() is emitting, so the model flip VOverlay makes on the same
// click-outside is not reported as a second close.
let closing = false

watch(innerValue, (val) => {
  emits('update:showDialog', val)
  // Escape is closed by VOverlay itself: it flips the model and nothing else, so
  // an owner listening only to `close` never hears about it. With
  // emitCloseOnEscape, report it the way the close button and click-outside
  // already are -- only while the owner still thinks the dialog is open, since a
  // close the owner drove through the prop is not news to it.
  if (props.emitCloseOnEscape && !val && !closing && props.showDialog) {
    emits('close')
  }
})
watch(() => props.showDialog, (newVal) => {
  innerValue.value = newVal
})

// Where focus goes back to when the dialog closes (WCAG 2.4.3). VOverlay returns
// focus to its activator, but most dialogs here are opened programmatically --
// a table's row action flips `show-dialog` -- so there is no activator, and
// closing (Escape above all) left focus on <body>. Remember whatever had focus
// when the dialog opened, and put focus back once it has fully left, if that
// element is still on the page and focus is lost: on <body>, or still inside
// this dialog's content, which after-leave fires just before removing. A dialog
// with an activator slot is left to VOverlay.
const slots = useSlots()
const card = useTemplateRef('card')
let returnFocusTo: HTMLElement | null = null
const rememberOpener = () => {
  if (!import.meta.client || slots.activator) return
  const active = document.activeElement
  returnFocusTo = active instanceof HTMLElement && active !== document.body ? active : null
}
// `sync`, so the opener is read before VOverlay moves focus into the dialog.
// ConfirmationDialog remounts this component on every open (its `key`), so a
// dialog that mounts already open is an open too.
if (props.showDialog) rememberOpener()
watch(() => props.showDialog, (open) => { if (open) rememberOpener() }, { flush: 'sync' })
// Focus has to be put back when it is nowhere useful: on <body>, or still
// inside this dialog (the card, or the VOverlay content wrapper that VDialog
// focuses after the enter transition, which is where Escape finds it when the
// user never tabbed in). Both get removed with the dialog.
const focusNeedsRestore = () => {
  const active = document.activeElement
  if (!active || active === document.body) return true
  const cardEl = card.value?.$el as HTMLElement | undefined
  const dialogEl = cardEl?.closest('.v-overlay__content') ?? cardEl
  return !!dialogEl?.contains(active)
}

// One-shot: whichever exit runs first (after-leave or unmount) takes the
// opener, so a single open never restores twice.
const takeFocusTarget = () => {
  const target = returnFocusTo
  returnFocusTo = null
  return target
}

const restoreFocus = () => {
  const target = takeFocusTarget()
  if (target?.isConnected && focusNeedsRestore()) {
    target.focus()
  }
}

// An owner that unmounts the dialog instead of closing it (v-if) never
// triggers after-leave. Decide here, while the dialog is still on the page,
// and move focus once VOverlay's focus trap is gone with the component --
// unless the owner has focused something else by then, which stays.
onBeforeUnmount(() => {
  const target = takeFocusTarget()
  if (!target || !focusNeedsRestore()) return
  nextTick(() => {
    if (target.isConnected && focusNeedsRestore()) target.focus()
  })
})

const close = () => {
  closing = true
  emits('update:showDialog', false)
  emits('close')
  // A macrotask, not nextTick: VOverlay flips the model right after emitting
  // click:outside, and nextTick's callback would run before that flip's watcher.
  setTimeout(() => { closing = false })
}
</script>

<style lang="scss" scoped>
.sticky-close {
  position: fixed;
  z-index: 10;
  margin-left: -24px;
}

.confirm-dialog {
  :deep(.v-card-actions) {
    min-height: auto;
    padding: 8px 16px;
  }
}

</style>
